import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  calculateScenario,
  FormatAdjustment,
  ScenarioMetrics,
  ScenarioResult,
  CapacityWarning,
} from '../../services/api';

export interface ScenarioState {
  siteId: string | null;
  adjustments: FormatAdjustment[];
  calculatedMetrics: ScenarioMetrics | null;
  warnings: CapacityWarning[];
  isModified: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: ScenarioState = {
  siteId: null,
  adjustments: [],
  calculatedMetrics: null,
  warnings: [],
  isModified: false,
  loading: false,
  error: null,
};

// Async thunks
export const calculateScenarioMetrics = createAsyncThunk(
  'scenario/calculate',
  async ({ siteId, adjustments }: { siteId: string; adjustments: FormatAdjustment[] }) => {
    return calculateScenario(siteId, adjustments);
  }
);

const scenarioSlice = createSlice({
  name: 'scenario',
  initialState,
  reducers: {
    initializeScenario: (
      state,
      action: PayloadAction<{ siteId: string; adjustments: FormatAdjustment[] }>
    ) => {
      state.siteId = action.payload.siteId;
      state.adjustments = action.payload.adjustments;
      state.isModified = false;
      state.warnings = [];
      state.calculatedMetrics = null;
    },
    updateAdjustment: (
      state,
      action: PayloadAction<{ format: string; adjustedValue: number }>
    ) => {
      const adjustment = state.adjustments.find((a) => a.format === action.payload.format);
      if (adjustment) {
        adjustment.adjustedValue = action.payload.adjustedValue;
        adjustment.delta = action.payload.adjustedValue - adjustment.baselineValue;
        state.isModified = true;
      }
    },
    resetScenario: (state) => {
      state.adjustments = state.adjustments.map((a) => ({
        ...a,
        adjustedValue: a.baselineValue,
        delta: 0,
      }));
      state.isModified = false;
      state.warnings = [];
    },
    clearScenario: () => initialState,
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(calculateScenarioMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateScenarioMetrics.fulfilled, (state, action: PayloadAction<ScenarioResult>) => {
        state.calculatedMetrics = action.payload.metrics;
        state.warnings = action.payload.warnings;
        state.loading = false;
      })
      .addCase(calculateScenarioMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to calculate scenario';
      });
  },
});

export const {
  initializeScenario,
  updateAdjustment,
  resetScenario,
  clearScenario,
  clearError,
} = scenarioSlice.actions;
export default scenarioSlice.reducer;
